/**
 * Nicknames: fun suggestions ("Swift Gecko 42" / "Vinnige Gekko 42") and a
 * validity check for leaderboard display names.
 *
 * The blocklist is a small first line of defence for a children's app, not a
 * moderation system: the server re-validates names, and anything that slips
 * through is handled by reporting/renaming. Suggested names never contain a
 * real name, which is why onboarding offers them first.
 */
import { deriveSeed, Rng, type Locale } from '@dropstack/core';
import { collapseSpaces, foldText } from './text.ts';

export const NICKNAME_MIN_LENGTH = 3;
export const NICKNAME_MAX_LENGTH = 20;
/** More digits than this looks like a phone number or other personal data. */
export const NICKNAME_MAX_DIGITS = 4;

// ───────────── Suggestions ─────────────

/** Attributive adjective forms (Afrikaans adjectives inflect before a noun: vinnig → vinnige). */
const ADJECTIVES: Readonly<Record<Locale, readonly string[]>> = {
  en: [
    'Swift', 'Brave', 'Clever', 'Happy', 'Jolly', 'Lucky', 'Mighty', 'Sunny', 'Cosmic', 'Nimble',
    'Plucky', 'Zippy', 'Bouncy', 'Wise', 'Speedy', 'Bright', 'Daring', 'Chirpy', 'Snappy', 'Curious',
  ],
  af: [
    'Vinnige', 'Dapper', 'Slim', 'Blye', 'Vrolike', 'Gelukkige', 'Kragtige', 'Sonnige', 'Kosmiese', 'Flukse',
    'Moedige', 'Ratse', 'Snaakse', 'Wyse', 'Speelse', 'Gawe', 'Blink', 'Wakker',
  ],
};

/** Southern African animals, mostly. */
const ANIMALS: Readonly<Record<Locale, readonly string[]>> = {
  en: [
    'Gecko', 'Meerkat', 'Springbok', 'Cheetah', 'Penguin', 'Dassie', 'Kudu', 'Zebra', 'Rhino', 'Hippo',
    'Pangolin', 'Hornbill', 'Tortoise', 'Lion', 'Leopard', 'Giraffe', 'Elephant', 'Warthog', 'Dolphin',
    'Otter', 'Owl', 'Eagle', 'Impala', 'Sunbird', 'Chameleon', 'Caracal',
  ],
  af: [
    'Gekko', 'Meerkat', 'Springbok', 'Jagluiperd', 'Pikkewyn', 'Dassie', 'Koedoe', 'Sebra', 'Renoster',
    'Seekoei', 'Ietermagô', 'Skilpad', 'Leeu', 'Luiperd', 'Kameelperd', 'Olifant', 'Vlakvark', 'Dolfyn',
    'Otter', 'Uil', 'Arend', 'Rooibok', 'Rooikat', 'Suikerbekkie',
  ],
};

/**
 * A fun, anonymous display name: adjective + animal + two-digit number.
 * Deterministic for a given (locale, seed); always passes isNicknameAllowed.
 */
export function suggestNickname(locale: Locale, seed: number): string {
  const rng = new Rng(seed);
  const adjective = rng.pick(ADJECTIVES[locale]);
  const number = rng.int(10, 99);
  const room = NICKNAME_MAX_LENGTH - adjective.length - ` ${number}`.length - 1;
  const fitting = ANIMALS[locale].filter((a) => a.length <= room);
  return `${adjective} ${rng.pick(fitting)} ${number}`;
}

/** `count` distinct suggestions (e.g. for the onboarding picker). */
export function suggestNicknames(locale: Locale, seed: number, count: number): string[] {
  const out = new Set<string>();
  for (let salt = 0; out.size < count && salt < count * 20; salt++) out.add(suggestNickname(locale, deriveSeed(seed, salt)));
  return [...out];
}

// ───────────── Validation ─────────────

export type NicknameProblem = 'too-short' | 'too-long' | 'invalid-chars' | 'no-letters' | 'too-many-digits' | 'not-allowed';

export interface NicknameCheck {
  ok: boolean;
  /** Why the name was refused (an i18n-friendly code). Absent when ok. */
  reason?: NicknameProblem;
}

/** The form a name is stored in: NFC, trimmed, single spaces. */
export function normalizeNickname(name: string): string {
  return collapseSpaces(name.normalize('NFC'));
}

/**
 * Check a display name after normalizeNickname(): 3–20 characters; letters
 * (any script, including ê/ë/ô), digits and single spaces only; at least one
 * letter; at most four digits (no phone numbers); nothing on the blocklist,
 * including leetspeak spellings ("sh1t") and stretched letters ("fuuuck").
 */
export function isNicknameAllowed(name: string): NicknameCheck {
  const n = normalizeNickname(name);
  const length = [...n].length;
  if (length < NICKNAME_MIN_LENGTH) return { ok: false, reason: 'too-short' };
  if (length > NICKNAME_MAX_LENGTH) return { ok: false, reason: 'too-long' };
  if (!/^[\p{L}\p{N} ]+$/u.test(n)) return { ok: false, reason: 'invalid-chars' };
  if (!/\p{L}/u.test(n)) return { ok: false, reason: 'no-letters' };
  if ((n.match(/\p{N}/gu)?.length ?? 0) > NICKNAME_MAX_DIGITS) return { ok: false, reason: 'too-many-digits' };
  if (containsBlockedTerm(n)) return { ok: false, reason: 'not-allowed' };
  return { ok: true };
}

/**
 * Terms blocked anywhere in the name, even across spaces ("f u c k").
 * Only unambiguous stems belong here: a short stem such as "ass" would block
 * "Classic" (the Scunthorpe problem), so those go in WHOLE_WORD_TERMS instead.
 */
const SUBSTRING_TERMS: readonly string[] = [
  // English
  'fuck', 'fuk', 'shit', 'cunt', 'bitch', 'whore', 'slut', 'nigg', 'porn', 'penis', 'vagina', 'dildo',
  'pussy', 'asshole', 'bastard', 'bollock', 'faggot', 'retard', 'hitler',
  // Afrikaans (including South African slurs)
  'kaffer', 'kaffir', 'fokof', 'fokken', 'fokking',
];

/**
 * Terms blocked as a whole word only: "hoer" must not block "Hoërskool", "fok"
 * must not block "fokus", "wank" must not block "Swanky", "poes" must not
 * block "poësie".
 */
const WHOLE_WORD_TERMS: ReadonlySet<string> = new Set([
  // English
  'ass', 'arse', 'dick', 'cock', 'tit', 'tits', 'piss', 'sex', 'sexy', 'nazi', 'twat', 'fag', 'rape', 'damn',
  'crap', 'wank', 'wanker',
  // Afrikaans
  'fok', 'hoer', 'poes', 'doos', 'kak', 'moer', 'naai', 'piel', 'tiet', 'drol',
]);

/**
 * Whole words are also compared with every repeated letter squashed
 * ("diiick" → "dick"), but only when the squashed word is at least this long:
 * shorter squashes turn real words into blocked ones ("kaak" → "kak").
 */
const MIN_SQUASHED_WORD = 4;

/** Digits and look-alikes commonly swapped for letters. "1" is ambiguous (i or l), so both readings are tried. */
const LEET: Readonly<Record<string, string>> = { '0': 'o', '3': 'e', '4': 'a', '5': 's', '6': 'g', '7': 't', '8': 'b', '9': 'g' };

function containsBlockedTerm(name: string): boolean {
  const folded = foldText(name);
  const readings = [deLeet(folded, 'i'), deLeet(folded, 'l'), folded.replace(/\d+/g, ' ')];
  return readings.some((reading) => {
    const words = reading.split(' ').filter(Boolean);
    const squashed = words.join('');
    const variants = [squashed, squashRepeats(squashed, 2), squashRepeats(squashed, 1)];
    if (SUBSTRING_TERMS.some((term) => variants.some((v) => v.includes(term)))) return true;
    return [...words, squashed].some((word) => {
      const squashedWord = squashRepeats(word, 1);
      const forms = squashedWord.length >= MIN_SQUASHED_WORD ? [word, squashRepeats(word, 2), squashedWord] : [word, squashRepeats(word, 2)];
      return forms.some((w) => WHOLE_WORD_TERMS.has(w));
    });
  });
}

/**
 * Blocklist check for free text that is shown publicly but is not a nickname:
 * school names and cities (both appear in public type-ahead and on
 * leaderboards). Unlike names, real place names are several ordinary words, so
 * words are NOT glued together (that would turn "Bush Itinerant" into
 * "…shit…"). Only runs of single letters are joined, which still catches
 * spaced-out spellings ("f u c k"). Leetspeak and stretched letters are
 * handled the same way as for nicknames.
 */
export function containsBlockedWords(text: string): boolean {
  const words = text
    .normalize('NFC')
    .toLowerCase()
    .split(/[^\p{L}\p{M}\p{N}]+/u)
    .filter((w) => w.length > 0 && !REAL_WORDS.has(w));
  return joinSingleLetters(words).some((word) => {
    const folded = foldText(word);
    return [deLeet(folded, 'i'), deLeet(folded, 'l'), folded.replace(/\d+/g, '')].some((reading) => {
      if (reading.length === 0) return false;
      const variants = [reading, squashRepeats(reading, 2), squashRepeats(reading, 1)];
      if (SUBSTRING_TERMS.some((term) => variants.some((v) => v.includes(term)))) return true;
      const squashed = squashRepeats(reading, 1);
      const forms = squashed.length >= MIN_SQUASHED_WORD ? variants : variants.slice(0, 2);
      return forms.some((w) => WHOLE_WORD_TERMS.has(w));
    });
  });
}

/**
 * Real words and place names (lower-case) that would otherwise trip the
 * blocklist: "Hoër" (as in "Hoër Volkskool") folds to "hoer", and is often
 * typed without the diaeresis on keyboards that lack ë; two English towns
 * contain a substring stem.
 */
const REAL_WORDS: ReadonlySet<string> = new Set(['hoër', 'hoer', 'scunthorpe', 'penistone']);

/** ["f", "u", "c", "k", "high"] → ["fuck", "high"]: single letters in a row become one word. */
function joinSingleLetters(words: readonly string[]): string[] {
  const out: string[] = [];
  let run = '';
  for (const w of words) {
    if ([...w].length === 1) {
      run += w;
      continue;
    }
    if (run) out.push(run);
    run = '';
    out.push(w);
  }
  if (run) out.push(run);
  return out;
}

function deLeet(s: string, one: 'i' | 'l'): string {
  return s.replace(/\d/g, (d) => (d === '1' ? one : (LEET[d] ?? d)));
}

/** Shorten runs of the same character to at most `max` ("fuuuck" → "fuck" at max 1, "asss" → "ass" at max 2). */
function squashRepeats(s: string, max: 1 | 2): string {
  return s.replace(max === 1 ? /(.)\1+/g : /(.)\1{2,}/g, max === 1 ? '$1' : '$1$1');
}
